import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

const THEME_KEY = 'okio-display-mode';

/**
 * Applies the theme to the document element
 * @param {'light' | 'dark' | 'system'} mode - The display mode preference
 */
const applyTheme = (mode) => {
  const root = document.documentElement;
  
  if (mode === 'dark') {
    root.classList.add('dark');
  } else if (mode === 'light') {
    root.classList.remove('dark');
  } else {
    // System mode - check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};

/**
 * Gets the initial theme from localStorage or defaults to 'system'
 */
const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'system';
  
  const stored = localStorage.getItem(THEME_KEY);
  if (stored && ['light', 'dark', 'system'].includes(stored)) {
    return stored;
  }
  return 'system';
};

export const ThemeProvider = ({ children }) => {
  const [displayMode, setDisplayModeState] = useState(getInitialTheme);
  
  // Apply theme on mount and when displayMode changes
  useEffect(() => {
    applyTheme(displayMode);
  }, [displayMode]);
  
  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (displayMode !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      applyTheme('system');
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [displayMode]);
  
  const setDisplayMode = useCallback((mode) => {
    setDisplayModeState(mode);
    localStorage.setItem(THEME_KEY, mode);
    applyTheme(mode);
  }, []);
  
  // Sync with user's app_settings when available
  const syncWithUserSettings = useCallback((userAppSettings) => {
    if (userAppSettings?.display_mode && ['light', 'dark', 'system'].includes(userAppSettings.display_mode)) {
      const newMode = userAppSettings.display_mode;
      // Only update if different from current
      if (newMode !== displayMode) {
        setDisplayModeState(newMode);
        localStorage.setItem(THEME_KEY, newMode);
        applyTheme(newMode);
      }
    }
  }, [displayMode]);
  
  const value = {
    displayMode,
    setDisplayMode,
    syncWithUserSettings,
    isDark: displayMode === 'dark' || 
            (displayMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches),
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;

